import { useEffect, useState } from 'react';
import { runtimeConfig } from './runtime';

const normalizePhoneDigits = (value: string): string => value.replace(/\D/g, '');

const toDialPhone = (value: string): string => {
  const digits = normalizePhoneDigits(value);

  if (!digits) {
    return '';
  }

  return digits.length === 9 ? `+351${digits}` : digits;
};

const toWhatsappNumber = (value: string): string => normalizePhoneDigits(toDialPhone(value));

const toMailtoHref = (email: string): string => `mailto:${email}`;

const createContactChannel = (email: string): PublicLegalContactChannel => ({
  email,
  mailtoHref: toMailtoHref(email),
});

const supportAvailabilityCodes = {
  weekdays0900To1800: 'Weekdays0900To1800',
} as const;

const resolveCurrentLanguage = (): string => {
  if (typeof navigator === 'undefined') {
    return 'en';
  }

  return navigator.language?.split('-')[0]?.toLowerCase() || 'en';
};

const formatSupportAvailability = (value: string, language = resolveCurrentLanguage()): string => {
  if (value !== supportAvailabilityCodes.weekdays0900To1800) {
    return value;
  }

  switch (language) {
    case 'pt':
      return 'Dias uteis, 09:00-18:00';
    case 'es':
      return 'Dias laborables, 09:00-18:00';
    case 'fr':
      return 'Jours ouvrables, 09:00-18:00';
    default:
      return 'Weekdays, 09:00-18:00';
  }
};

export type PublicLegalContactChannel = {
  email: string;
  mailtoHref: string;
};

export type PublicLegalContacts = {
  generalEmail: string;
  supportEmail: string;
  privacyEmail: string;
  complaintsEmail: string;
  supportAvailability: string;
  phonePublished: boolean;
  whatsappEnabled: boolean;
  phone?: string | null;
  phoneHref?: string | null;
  whatsappUrl?: string | null;
  general: PublicLegalContactChannel;
  support: PublicLegalContactChannel;
  privacy: PublicLegalContactChannel;
  complaints: PublicLegalContactChannel;
};

export type PublicLegalCommercialOfferings = {
  proType: string;
  basicType: string;
  proIncludedFeatureCodes: string[];
  proUsageLimitCodes: string[];
  serviceRequirementCodes: string[];
  acceptedPaymentMethods: string[];
  creditsHaveStandaloneMonetaryValue: boolean;
  creditsAreTransferable: boolean;
  creditsSoldSeparately: boolean;
  unusedCreditsExpireAtCycleEnd: boolean;
  serviceStartsAfterPaymentConfirmation: boolean;
  maximumProvisionDelayHours: number;
  automaticRenewal: boolean;
  annualPlanCreditsPerMonth: number;
  requiredDisclaimers: string[];
};

export type PublicLegalLifecycle = {
  monthlyDuration: string;
  annualDuration: string;
  gracePeriodDays: number;
  selfServiceCancellation: boolean;
  refundCancellationEndsProImmediately: boolean;
  normalExpirationEndsAccessAtPeriodEnd: boolean;
  deleteAccountOnCancellation: boolean;
};

export type PublicLegalConfiguration = {
  version: string;
  entity: {
    brandName: string;
    website: string;
  };
  contacts: PublicLegalContacts;
  commercialOfferings: PublicLegalCommercialOfferings;
  lifecycle: PublicLegalLifecycle;
  legalPages: typeof runtimeConfig.legal.canonicalUrls;
  externalUrls: typeof runtimeConfig.legal.externalUrls;
};

export type LegalPageKey = keyof typeof runtimeConfig.legal.slugs;

export type PublicLegalPageLink = {
  label: string;
  url: string;
  isExternal: boolean;
};

export type PublicLegalPageSection = {
  title: string;
  paragraphs: string[];
  bullets: string[];
  links: PublicLegalPageLink[];
};

export type PublicLegalPageContent = {
  pageType: string;
  slug: string;
  language: string;
  version: string;
  lastUpdated: string;
  lastUpdatedLabel: string;
  title: string;
  shortDescription: string;
  intro: string;
  canonicalUrl: string;
  sections: PublicLegalPageSection[];
};

export const fallbackPublicLegalConfiguration: PublicLegalConfiguration = Object.freeze({
  version: 'frontend-runtime-fallback-v1',
  entity: Object.freeze({
    brandName: 'Senti',
    website: runtimeConfig.appOrigin,
  }),
  contacts: Object.freeze({
    generalEmail: runtimeConfig.support.email,
    supportEmail: runtimeConfig.support.email,
    privacyEmail: runtimeConfig.support.email,
    complaintsEmail: runtimeConfig.support.email,
    supportAvailability: formatSupportAvailability(supportAvailabilityCodes.weekdays0900To1800),
    phonePublished: Boolean(runtimeConfig.support.phone),
    whatsappEnabled: Boolean(runtimeConfig.support.phone),
    phone: runtimeConfig.support.phone,
    phoneHref: runtimeConfig.support.phone ? `tel:${toDialPhone(runtimeConfig.support.phone)}` : null,
    whatsappUrl: runtimeConfig.support.phone
      ? `https://wa.me/${toWhatsappNumber(runtimeConfig.support.phone)}`
      : null,
    general: createContactChannel(runtimeConfig.support.email),
    support: createContactChannel(runtimeConfig.support.email),
    privacy: createContactChannel(runtimeConfig.support.email),
    complaints: createContactChannel(runtimeConfig.support.email),
  }),
  commercialOfferings: Object.freeze({
    proType: 'PaidDigitalSubscription',
    basicType: 'FreeDigitalService',
    proIncludedFeatureCodes: [
      'AllBasicFeatures',
      'Dashboard',
      'NatalChart',
      'PersonalizedInsights',
      'AiChat',
    ],
    proUsageLimitCodes: [
      'CreditsLimitedByPlan',
      'AiUsageSubjectToProviderAvailability',
      'MarketInformationIsInformationalOnly',
    ],
    serviceRequirementCodes: [
      'ActiveAccountRequired',
      'AuthenticationRequired',
      'InternetAccessRequired',
      'ExternalProvidersRequired',
    ],
    acceptedPaymentMethods: ['CARD'],
    creditsHaveStandaloneMonetaryValue: false,
    creditsAreTransferable: false,
    creditsSoldSeparately: false,
    unusedCreditsExpireAtCycleEnd: true,
    serviceStartsAfterPaymentConfirmation: true,
    maximumProvisionDelayHours: 24,
    automaticRenewal: false,
    annualPlanCreditsPerMonth: 100,
    requiredDisclaimers: [
      'Astrology',
      'ArtificialIntelligence',
      'MarketAndInvestmentInformation',
    ],
  }),
  lifecycle: Object.freeze({
    monthlyDuration: 'P1M',
    annualDuration: 'P1Y',
    gracePeriodDays: 0,
    selfServiceCancellation: false,
    refundCancellationEndsProImmediately: true,
    normalExpirationEndsAccessAtPeriodEnd: true,
    deleteAccountOnCancellation: false,
  }),
  legalPages: runtimeConfig.legal.canonicalUrls,
  externalUrls: runtimeConfig.legal.externalUrls,
});

let cachedPublicLegalConfiguration: PublicLegalConfiguration | null = null;
const cachedPublicLegalPageContent: Record<string, PublicLegalPageContent> = {};
const supportedPublicLegalLanguages = ['en', 'pt', 'es', 'fr'] as const;

const normalizePublicLegalConfiguration = (
  configuration: Partial<PublicLegalConfiguration> | null | undefined
): PublicLegalConfiguration => ({
  version: configuration?.version || fallbackPublicLegalConfiguration.version,
  entity: {
    ...fallbackPublicLegalConfiguration.entity,
    ...configuration?.entity,
  },
  contacts: normalizePublicLegalContacts(configuration?.contacts),
  commercialOfferings: normalizePublicLegalCommercialOfferings(configuration?.commercialOfferings),
  lifecycle: normalizePublicLegalLifecycle(configuration?.lifecycle),
  legalPages: {
    ...fallbackPublicLegalConfiguration.legalPages,
    ...configuration?.legalPages,
  },
  externalUrls: {
    ...fallbackPublicLegalConfiguration.externalUrls,
    ...configuration?.externalUrls,
  },
});

const normalizePublicLegalContactChannel = (
  channel: Partial<PublicLegalContactChannel> | null | undefined,
  fallbackEmail: string
): PublicLegalContactChannel => {
  const email = channel?.email || fallbackEmail;

  return {
    email,
    mailtoHref: channel?.mailtoHref || toMailtoHref(email),
  };
};

const normalizePublicLegalContacts = (
  contacts: Partial<PublicLegalContacts> | null | undefined
): PublicLegalContacts => {
  const merged = {
    ...fallbackPublicLegalConfiguration.contacts,
    ...contacts,
  };

  return {
    ...merged,
    supportAvailability: formatSupportAvailability(merged.supportAvailability),
    phonePublished: contacts?.phonePublished ?? Boolean(merged.phone && merged.phoneHref),
    whatsappEnabled: contacts?.whatsappEnabled ?? Boolean(merged.whatsappUrl),
    general: normalizePublicLegalContactChannel(contacts?.general, merged.generalEmail),
    support: normalizePublicLegalContactChannel(contacts?.support, merged.supportEmail),
    privacy: normalizePublicLegalContactChannel(contacts?.privacy, merged.privacyEmail),
    complaints: normalizePublicLegalContactChannel(contacts?.complaints, merged.complaintsEmail),
  };
};

const normalizeStringArray = (value: unknown, fallback: string[]): string[] => {
  if (!Array.isArray(value)) {
    return fallback;
  }

  const normalized = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);

  return normalized.length > 0 ? normalized : fallback;
};

const normalizeNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const normalizeBoolean = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const normalizePublicLegalCommercialOfferings = (
  commercialOfferings: Partial<PublicLegalCommercialOfferings> | null | undefined
): PublicLegalCommercialOfferings => {
  const fallback = fallbackPublicLegalConfiguration.commercialOfferings;
  const merged = {
    ...fallback,
    ...commercialOfferings,
  };

  return {
    ...merged,
    proIncludedFeatureCodes: normalizeStringArray(commercialOfferings?.proIncludedFeatureCodes, fallback.proIncludedFeatureCodes),
    proUsageLimitCodes: normalizeStringArray(commercialOfferings?.proUsageLimitCodes, fallback.proUsageLimitCodes),
    serviceRequirementCodes: normalizeStringArray(commercialOfferings?.serviceRequirementCodes, fallback.serviceRequirementCodes),
    acceptedPaymentMethods: normalizeStringArray(commercialOfferings?.acceptedPaymentMethods, fallback.acceptedPaymentMethods),
    creditsHaveStandaloneMonetaryValue: normalizeBoolean(
      commercialOfferings?.creditsHaveStandaloneMonetaryValue,
      fallback.creditsHaveStandaloneMonetaryValue
    ),
    creditsAreTransferable: normalizeBoolean(commercialOfferings?.creditsAreTransferable, fallback.creditsAreTransferable),
    creditsSoldSeparately: normalizeBoolean(commercialOfferings?.creditsSoldSeparately, fallback.creditsSoldSeparately),
    unusedCreditsExpireAtCycleEnd: normalizeBoolean(
      commercialOfferings?.unusedCreditsExpireAtCycleEnd,
      fallback.unusedCreditsExpireAtCycleEnd
    ),
    serviceStartsAfterPaymentConfirmation: normalizeBoolean(
      commercialOfferings?.serviceStartsAfterPaymentConfirmation,
      fallback.serviceStartsAfterPaymentConfirmation
    ),
    maximumProvisionDelayHours: normalizeNumber(
      commercialOfferings?.maximumProvisionDelayHours,
      fallback.maximumProvisionDelayHours
    ),
    automaticRenewal: normalizeBoolean(commercialOfferings?.automaticRenewal, fallback.automaticRenewal),
    annualPlanCreditsPerMonth: normalizeNumber(
      commercialOfferings?.annualPlanCreditsPerMonth,
      fallback.annualPlanCreditsPerMonth
    ),
    requiredDisclaimers: normalizeStringArray(commercialOfferings?.requiredDisclaimers, fallback.requiredDisclaimers),
  };
};

const normalizePublicLegalLifecycle = (
  lifecycle: Partial<PublicLegalLifecycle> | null | undefined
): PublicLegalLifecycle => {
  const fallback = fallbackPublicLegalConfiguration.lifecycle;
  const merged = {
    ...fallback,
    ...lifecycle,
  };

  return {
    ...merged,
    monthlyDuration: lifecycle?.monthlyDuration || fallback.monthlyDuration,
    annualDuration: lifecycle?.annualDuration || fallback.annualDuration,
    gracePeriodDays: normalizeNumber(lifecycle?.gracePeriodDays, fallback.gracePeriodDays),
    selfServiceCancellation: normalizeBoolean(lifecycle?.selfServiceCancellation, fallback.selfServiceCancellation),
    refundCancellationEndsProImmediately: normalizeBoolean(
      lifecycle?.refundCancellationEndsProImmediately,
      fallback.refundCancellationEndsProImmediately
    ),
    normalExpirationEndsAccessAtPeriodEnd: normalizeBoolean(
      lifecycle?.normalExpirationEndsAccessAtPeriodEnd,
      fallback.normalExpirationEndsAccessAtPeriodEnd
    ),
    deleteAccountOnCancellation: normalizeBoolean(
      lifecycle?.deleteAccountOnCancellation,
      fallback.deleteAccountOnCancellation
    ),
  };
};

export const loadPublicLegalConfiguration = async (
  signal?: AbortSignal
): Promise<PublicLegalConfiguration> => {
  if (cachedPublicLegalConfiguration) {
    return cachedPublicLegalConfiguration;
  }

  if (typeof fetch !== 'function') {
    return fallbackPublicLegalConfiguration;
  }

  const response = await fetch(runtimeConfig.legal.publicConfigUrl, {
    headers: {
      Accept: 'application/json',
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to load public legal configuration: ${response.status}`);
  }

  cachedPublicLegalConfiguration = normalizePublicLegalConfiguration(await response.json());
  return cachedPublicLegalConfiguration;
};

const normalizeStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string');
};

const normalizePublicLegalPageContent = (
  page: Partial<PublicLegalPageContent> | null | undefined
): PublicLegalPageContent => {
  const candidateSections = page?.sections;
  const sections = Array.isArray(candidateSections) ? candidateSections : [];

  return {
    pageType: page?.pageType || '',
    slug: page?.slug || '',
    language: page?.language || 'pt',
    version: page?.version || '',
    lastUpdated: page?.lastUpdated || '',
    lastUpdatedLabel: page?.lastUpdatedLabel || '',
    title: page?.title || '',
    shortDescription: page?.shortDescription || '',
    intro: page?.intro || '',
    canonicalUrl: page?.canonicalUrl || '',
    sections: sections.map(section => ({
      title: section.title || '',
      paragraphs: normalizeStringList(section.paragraphs),
      bullets: normalizeStringList(section.bullets),
      links: Array.isArray(section.links)
        ? section.links.map(link => ({
            label: link.label || '',
            url: link.url || '',
            isExternal: Boolean(link.isExternal),
          }))
        : [],
    })),
  };
};

export const buildPublicLegalPageContentUrl = (slug: string): string =>
  new URL(encodeURIComponent(slug.trim()), runtimeConfig.legal.publicPageContentBaseUrl).toString();

export const normalizePublicLegalLanguage = (language?: string | null): string => {
  const normalizedLanguage = language?.trim().split('-')[0]?.toLowerCase();

  if (supportedPublicLegalLanguages.includes(normalizedLanguage as typeof supportedPublicLegalLanguages[number])) {
    return normalizedLanguage as typeof supportedPublicLegalLanguages[number];
  }

  return 'pt';
};

export const loadPublicLegalPageContent = async (
  slug: string,
  language?: string | null,
  signal?: AbortSignal
): Promise<PublicLegalPageContent | null> => {
  const normalizedSlug = slug.trim().toLowerCase();
  const normalizedLanguage = normalizePublicLegalLanguage(language);

  if (!normalizedSlug) {
    return null;
  }

  const cacheKey = `${normalizedSlug}:${normalizedLanguage}`;
  const cachedPage = cachedPublicLegalPageContent[cacheKey];
  if (cachedPage) {
    return cachedPage;
  }

  if (typeof fetch !== 'function') {
    return null;
  }

  const response = await fetch(buildPublicLegalPageContentUrl(normalizedSlug), {
    headers: {
      Accept: 'application/json',
      'Accept-Language': normalizedLanguage,
    },
    signal,
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to load public legal page content: ${response.status}`);
  }

  const page = normalizePublicLegalPageContent(await response.json());
  cachedPublicLegalPageContent[cacheKey] = page;
  return page;
};

export const usePublicLegalConfiguration = (): PublicLegalConfiguration => {
  const [configuration, setConfiguration] = useState<PublicLegalConfiguration>(
    fallbackPublicLegalConfiguration
  );

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    loadPublicLegalConfiguration(controller.signal)
      .then(nextConfiguration => {
        if (isMounted) {
          setConfiguration(nextConfiguration);
        }
      })
      .catch(() => {
        if (isMounted) {
          setConfiguration(fallbackPublicLegalConfiguration);
        }
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  return configuration;
};
